import { ElForm, ElFormItem } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { UCenterServices } from 'network'
import { DefineComponentProps, encryptPassword } from 'utils'
import { CustomButton, HocElDialog, UiUtils } from '..'
import { DialogHeader } from './DialogHeader'
import { SecurityBottom } from './SecurityBottom'
import { SizedBox } from './SizedBox'
import { TradePasswordInput } from './VerifyInput'
import './ConfirmPassword.scss'

export type DefaultConfirmPasswordResult = {
  password: string
  data: boolean
}

export type ConfirmPasswordType = 'withdraw' | 'tradePwd'

export enum ConfirmPasswordTypeMap {
  Withdraw = 'withdraw',
  TradePwd = 'tradePwd',
}

type PreConfirmPasswordProps<T = DefaultConfirmPasswordResult, P extends ConfirmPasswordType = 'tradePwd'> = {
  title: string
  validMessage?: string
  /** 自定义的密码验证逻辑 */
  checkTradePwd?: (pwd: string) => FetchResponse<T>
  onResolve: (data: T) => Promise<void>
  onReject: () => void
  onClose: () => void
  modelValue: boolean
  confirmText?: string
  type?: P
}

type WithdrawConfirmPasswordProps = {
  tokenWithdraw: string
}

export type ConfirmPasswordProps<T = DefaultConfirmPasswordResult, P extends ConfirmPasswordType = 'tradePwd'> = PreConfirmPasswordProps<T, P> &
  (P extends 'withdraw' ? WithdrawConfirmPasswordProps : Partial<WithdrawConfirmPasswordProps>)

export function ConfirmPassword<T = DefaultConfirmPasswordResult>(props: ConfirmPasswordProps<T>) {
  const _ConfirmPassword = defineComponent<ConfirmPasswordProps<T>>({
    props: DefineComponentProps<ConfirmPasswordProps<T>>([
      'title',
      'onResolve',
      'modelValue',
      'confirmText',
      'onClose',
      'onReject',
      'checkTradePwd',
      'type',
      'tokenWithdraw',
      'validMessage',
    ]),
    setup(props) {
      const { t } = useI18n()
      const type = props.type || ('tradePwd' as ConfirmPasswordType)
      const modelValue = ref(props.modelValue)
      const password = ref('')

      const loading = ref(false)

      const onClose = () => {
        modelValue.value = false
        setTimeout(() => {
          props.onClose?.()
        }, 300)
      }

      const checkTradePwd = async (pwd: string) => {
        if (props.checkTradePwd) {
          return props.checkTradePwd(encryptPassword(pwd))
        }
        if (type === 'withdraw') {
          const res = UCenterServices.withdrawCheckTradePwdVerifyToken({
            tradePwd: encryptPassword(pwd),
            tokenWithdraw: (props as ConfirmPasswordProps<T, 'withdraw'>).tokenWithdraw,
          })
          return res
        }
        return UCenterServices.checkTradePwd({
          tradePwd: encryptPassword(pwd),
        })
      }

      const onConfirmPassword = async () => {
        const verifyCode = password.value
        if (!verifyCode || verifyCode.length < 6) {
          UiUtils.showError(t(props.validMessage || '请输入支付密码'))
          return
        }
        if (loading.value) return
        loading.value = true

        const res = await checkTradePwd(verifyCode)
        loading.value = false
        if (res.status === 200) {
          modelValue.value = false
          await props.onResolve({ password: encryptPassword(verifyCode), data: res.data } as T)
          password.value = ''
        } else {
          UiUtils.showError(res.msg)
          password.value = ''
        }
      }

      return () => (
        <HocElDialog v-model={modelValue.value} showClose={false} closeOnClickModal={false} width={400} placement={'bottom'}>
          {{
            header: () => <DialogHeader onClose={onClose}>{t(props.title || '支付密码')}</DialogHeader>,
            default: () => (
              <div class="confirm-password-layout">
                <SizedBox height={12} />
                <ElForm labelPosition="top">
                  <ElFormItem prop="password">
                    <div class="flex justify-center w-full">
                      <TradePasswordInput v-model={password.value} onInputComplete={onConfirmPassword} isPassword />
                    </div>
                  </ElFormItem>
                  <SizedBox height={12} />
                  <ElFormItem style={{ marginBottom: 0 }}>
                    <CustomButton class={'confirm-password-button'} type="primary" loading={loading.value} onClick={onConfirmPassword}>
                      {t(props.confirmText || '确认支付')}
                    </CustomButton>
                  </ElFormItem>
                  <SecurityBottom />
                </ElForm>
              </div>
            ),
          }}
        </HocElDialog>
      )
    },
  })

  return <_ConfirmPassword {...props} />
}
